import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, saveBlobAs } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Download, FileText, Play } from "lucide-react";
import { toast } from "sonner";

export default function SessionDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [d, cl] = await Promise.all([api.getSession(id), api.getChecklist()]);
        setData(d);
        setChecklist(cl);
      } catch (e) {
        toast.error("Failed to load session");
      }
    })();
  }, [id]);

  if (!data || !checklist) {
    return <div className="p-10 text-center text-slate-500">Loading session…</div>;
  }

  const { session, results } = data;
  const byId = Object.fromEntries(results.map((r) => [r.test_id, r]));

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <Link
            to="/dashboard"
            data-testid="detail-back-link"
            className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900 mb-3"
          >
            <ArrowLeft size={12} className="mr-1" /> All sessions
          </Link>
          <div className="overline mb-1">Session detail</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            {session.tester_name}
          </h1>
          <div className="mt-2 text-sm text-slate-600 font-mono">
            {session.browser || "—"} · {session.device || "—"} · Started{" "}
            {new Date(session.started_at).toLocaleString()}
          </div>
        </div>
        <div className="flex gap-2">
          {session.status === "in_progress" && (
            <Link to={`/session/${id}/run`}>
              <Button
                data-testid="detail-resume-btn"
                className="bg-keplerio-teal-dark hover:bg-keplerio-navy text-white rounded-none h-10"
              >
                <Play size={14} className="mr-2" /> Resume run
              </Button>
            </Link>
          )}
          <Button
            variant="outline"
            data-testid="detail-export-btn"
            onClick={async () => {
              try {
                const res = await api.downloadSessionCsv(id);
                saveBlobAs(res.data, `uat-session-${id.slice(0, 8)}.csv`);
              } catch {
                toast.error("Failed to export CSV");
              }
            }}
            className="rounded-none border-slate-300 h-10"
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
          <Button
            data-testid="detail-export-pdf-btn"
            onClick={async () => {
              try {
                const res = await api.downloadSessionPdf(id);
                saveBlobAs(res.data, `uat-session-${id.slice(0, 8)}.pdf`);
              } catch {
                toast.error("Failed to export PDF");
              }
            }}
            className="rounded-none bg-keplerio-navy hover:bg-keplerio-navy-dark text-white h-10"
          >
            <FileText size={14} className="mr-2" /> PDF report
          </Button>
        </div>
      </div>

      {session.overall_recommendation && (
        <div className="bg-white border border-slate-200 p-6">
          <div className="overline mb-2">Overall recommendation</div>
          <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">
            {session.overall_recommendation}
          </p>
        </div>
      )}

      {checklist.sections.map((section) => (
        <section key={section.id} className="space-y-3">
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-slate-400 text-sm">
              Section {section.number}
            </span>
            <h2 className="font-heading text-xl font-semibold tracking-tight">
              {section.title}
            </h2>
          </div>
          <div className="bg-white border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-slate-200">
                  <TableHead className="overline text-slate-500 w-[140px]">ID</TableHead>
                  <TableHead className="overline text-slate-500">Title</TableHead>
                  <TableHead className="overline text-slate-500 w-[110px]">
                    Result
                  </TableHead>
                  <TableHead className="overline text-slate-500">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {section.cases.map((c) => {
                  const r = byId[c.id];
                  return (
                    <TableRow
                      key={c.id}
                      data-testid={`case-row-${c.id}`}
                      className="border-b border-slate-100"
                    >
                      <TableCell className="font-mono text-xs">{c.id}</TableCell>
                      <TableCell className="text-sm text-slate-800">{c.title}</TableCell>
                      <TableCell>
                        <ResultBadge status={r?.status} />
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 whitespace-pre-line">
                        {r?.notes || "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </section>
      ))}
    </div>
  );
}

function ResultBadge({ status }) {
  if (!status) {
    return (
      <span className="text-xs text-slate-400 font-mono">not run</span>
    );
  }
  const map = {
    pass: { label: "PASS", cls: "bg-emerald-600 text-white" },
    fail: { label: "FAIL", cls: "bg-red-600 text-white" },
    skipped: { label: "SKIP", cls: "bg-slate-200 text-slate-800" },
    blocked: { label: "BLOCK", cls: "bg-amber-500 text-white" },
  }[status] || { label: status, cls: "bg-slate-200 text-slate-800" };
  return (
    <Badge className={`rounded-none font-mono text-[10px] tracking-widest ${map.cls}`}>
      {map.label}
    </Badge>
  );
}
